import { zodResolver } from '@hookform/resolvers/zod'
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  Grid,
  Stack,
} from '@mui/joy'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '~/shared/ui/Buttons'
import {
  Checkbox,
  DatePickerInput,
  FormProvider,
  TextInput,
} from '~/shared/ui/Form'
import { FormValues, formSchema } from './model'

type EditFormProps = {
  defaultValues?: Partial<FormValues>
  onSuccess?: (formValues: FormValues) => void
  isSubmitting?: boolean
  canEdit?: boolean
}

export function EditForm({
  defaultValues,
  onSuccess,
  isSubmitting,
  canEdit,
}: EditFormProps) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const disabled = !isEditing || isSubmitting

  const form = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(formSchema),
  })

  const { reset, watch, trigger, formState, setValue } = form
  const isTemporaryNumber = watch('isTemporaryNumber')

  const handleOnSuccess = useCallback(
    (formValues: FormValues) => {
      onSuccess?.(formValues)
    },
    [onSuccess],
  )

  const handleButtonsShow = () => {
    if (!canEdit) return null
    setIsEditing(true)
  }

  const handleButtonsHidden = () => {
    setIsEditing(false)
  }

  const handleCancel = () => {
    handleButtonsHidden()
  }

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type !== 'change' || name !== 'isTemporaryNumber') return

      value.isTemporaryNumber
        ? setValue('issuedBy', '')
        : setValue('expirationDate', '')

      if (formState.submitCount) trigger()
    })

    return () => subscription.unsubscribe()
  }, [watch, setValue, formState.submitCount, trigger])

  useEffect(() => {
    if (defaultValues) reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  useEffect(() => {
    if (!isSubmitting) handleButtonsHidden()
  }, [isSubmitting])

  return (
    <FormProvider form={form} onSuccess={handleOnSuccess}>
      <Grid container spacing={2.5} onDoubleClick={handleButtonsShow}>
        <Grid xs={6}>
          <TextInput
            label='form.license plate number'
            name='plateNumber'
            placeholder='form.enter license plate number'
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={6}>
          <Checkbox
            labelI18n='form.temporary plate'
            name='isTemporaryNumber'
            readOnly={disabled}
            formControlProps={{ sx: { marginTop: '40px' } }}
          />
        </Grid>
        <Grid xs={4}>
          <TextInput
            label='form.number'
            name='number'
            placeholder='form.enter number'
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={4}>
          <DatePickerInput
            label='form.issue date'
            name='issuedDate'
            placeholder='form.select date'
            readOnly={disabled}
          />
        </Grid>

        {isTemporaryNumber && (
          <Grid xs={4}>
            <DatePickerInput
              label='form.valid until'
              name='expirationDate'
              placeholder='form.select date'
              readOnly={disabled}
            />
          </Grid>
        )}

        {!isTemporaryNumber && (
          <Grid xs={4}>
            <TextInput
              label='form.traffic police department code'
              name='issuedBy'
              placeholder='form.enter traffic police department code'
              readOnly={disabled}
            />
          </Grid>
        )}

        <Grid xs={12}>
          <AccordionGroup>
            <Accordion expanded={isEditing} sx={{ minHeight: 0, padding: 0 }}>
              <AccordionDetails>
                <Stack direction='row' spacing={2}>
                  <Button
                    variant='red'
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    {t('button.cancel')}
                  </Button>

                  <Button
                    variant='green'
                    type='submit'
                    loading={isSubmitting}
                    sx={{ marginRight: '24px' }}
                  >
                    {t('button.save')}
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </AccordionGroup>
        </Grid>
      </Grid>
    </FormProvider>
  )
}
